import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import CancelIcon from "@material-ui/icons/Cancel";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import GetAppIcon from "@material-ui/icons/GetApp";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import * as XLSX from 'xlsx';

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1400px",
  },
  form: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    rowGap: `${theme.spacing(1)}px`,
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    rowGap: `${theme.spacing(1)}px`,
    columnGap: `${theme.spacing(1)}px`,
    alignItems: "center",
  },
  activeFilters: {
    display: "flex",
    flexWrap: "wrap" as const,
    rowGap: `${theme.spacing(1)}px`,
    columnGap: `${theme.spacing(1)}px`,
    alignItems: "center",
  },
  activeFiltersLabel: {
    fontSize: "0.8rem",
    color: theme.palette.grey[600],
    marginRight: theme.spacing(0.5),
  },
  input: {
    minWidth: 240,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: "70vh",
    overflow: "auto",
  },
  table: {
    minWidth: 1800,
    borderCollapse: "separate",
    tableLayout: "fixed" as const,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
    width: 130,
    wordBreak: "break-word" as const,
  },
  stickyCell: {
    position: "sticky" as const,
    backgroundColor: "#fff",
    zIndex: 2,
  },
  stickyHeaderCell: {
    position: "sticky" as const,
    backgroundColor: theme.palette.grey[100],
    zIndex: 4,
  },
  toggleColumnsBtn: {
    position: "sticky" as const,
    right: 0,
    zIndex: 4,
    backgroundColor: theme.palette.grey[100],
  },
  toggleColumnsBtnBody: {
    position: "sticky" as const,
    right: 0,
    zIndex: 2,
    backgroundColor: "#fff",
  },
  copyableText: {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      color: "#1976d2",
    },
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  statusChip: {
    fontSize: "0.75rem",
    height: "24px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    color: theme.palette.grey[500],
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: theme.spacing(1),
  },
  dialogContent: {
    minWidth: 400,
    minHeight: 200,
  },
  jsonContent: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: 4,
    overflow: "auto",
    maxHeight: 400,
    fontFamily: "monospace",
    fontSize: "0.875rem",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  modalLoadingContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
}));

interface GiftCardDetail {
  id: number;
  user_id: number;
  brand_name: string | null;
  amount: string | number;
  payment_created_date: string;
  transaction_status: string | null;
  ryft_payment_status: string | null;
  simp_id: string;
  provider_session_id: string;
  remitter_provider_id: string;
  reference: string | null;
  bank_reference: string | null;
  sprive_cancelled: number | null;
  is_refunded: number | null;
  source: string | null;
  payment_authorised_date: string | null;
  payment_execution_date: string | null;
  client_reference_id: string | null;
  purchase_status: string | null;
  rewards_amount: string | number | null;
  is_inflight: number | null;
  url: string | null;
  gift_card_purchase_id: number | null;
  transaction_type: "purchase" | "top_up" | "mastercard_anywhere" | null;
  balance_enquiry_url: string | null;
  has_balance_check: number;
  topup_status: string | null;
}

interface GiftCardDetailsResponse {
  total: number;
  limit: number;
  skip: number;
  data: any[];
}

interface GiftCardDetailsHeader {
  [key: string]: string;
  ID: string;
  USER_ID: string;
  BRAND: string;
  AMOUNT: string;
  PAYMENT_CREATED_DATE: string;
  TRANSACTION_STATUS: string;
  TRANSACTION_TYPE: string;
  RYFT_PAYMENT_STATUS: string;
  SIMP_ID: string;
  PROVIDER: string;
  BANK_REFERENCE: string;
  REFERENCE: string;
  IS_SPRIVE_CANCELLED: string;
  REFUNDED: string;
  SOURCE: string;
  PAYMENT_AUTHORISED_DATE: string;
  PAYMENT_EXECUTION_DATE: string;
  CLIENT_REF: string;
  PURCHASE_STATUS: string;
  REWARDS_AMOUNT: string;
  IN_FLIGHT: string;
  URL: string;
  GIFT_CARD_PURCHASE_ID: string;
  TOPUP_STATUS: string;
}

const EXPANDABLE_KEYS = new Set([
  "ID", "USER_ID", "PAYMENT_AUTHORISED_DATE", "PAYMENT_EXECUTION_DATE",
  "IN_FLIGHT", "GIFT_CARD_PURCHASE_ID",
]);

// Not rendered as a column - transaction_type only drives the ↳ prefix on BRAND.
const HIDDEN_KEYS = new Set(["TRANSACTION_TYPE"]);

const FROZEN_COLUMN_COUNT = 4;
const FROZEN_COLUMN_WIDTHS = [130, 100, 170, 120];
const FROZEN_LEFT_OFFSETS = FROZEN_COLUMN_WIDTHS.map((_, i) =>
  FROZEN_COLUMN_WIDTHS.slice(0, i).reduce((sum, w) => sum + w, 0)
);

const STICKY_KEYS = ["BRAND", "AMOUNT", "PAYMENT_CREATED_DATE", "TRANSACTION_STATUS"];

const UPPERCASE_DISPLAY_KEYS = new Set(["reference", "account_holder_name"]);
const uppercaseDisplayFields = (value: any): any => {
  if (Array.isArray(value)) return value.map(uppercaseDisplayFields);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) =>
        UPPERCASE_DISPLAY_KEYS.has(k) && typeof v === "string"
          ? [k, v.toUpperCase()]
          : [k, uppercaseDisplayFields(v)]
      )
    );
  }
  return value;
};
const getRowKey = (detail: GiftCardDetail) => `${detail.transaction_type}-${detail.id}`;

const GiftCardTransactionDetailsPage = () => {
  const classes = useStyles();
  // const translate = useTranslate();
  const notify = useNotify();

  const [email, setEmail] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [refundedFilter, setRefundedFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [purchaseTypeFilter, setPurchaseTypeFilter] = useState("");
  const [paymentTypeFilter, setPaymentTypeFilter] = useState("");
  const [initiatedByFilter, setInitiatedByFilter] = useState("");
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [pageSize, setPageSize] = useState<number>(10);
  const [allGiftCardDetails, setAllGiftCardDetails] = useState<GiftCardDetail[]>([]);
  const [tableHeader, setTableHeader] = useState<GiftCardDetailsHeader | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [providerStatusModalOpen, setProviderStatusModalOpen] = useState(false);
  const [providerStatusLoading, setProviderStatusLoading] = useState(false);
  const [providerStatusData, setProviderStatusData] = useState<any>(null);
  const [providerStatusError, setProviderStatusError] = useState<string | null>(null);
  const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
  const [showExtraColumns, setShowExtraColumns] = useState(false);
  const [balanceResults, setBalanceResults] = useState<Record<string, {
    loading: boolean;
    balance: number | null;
    unavailable: boolean;
  }>>({});


  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      notify("Copied to clipboard", "info");
    });
  };

  const fetchGiftCardDetails = async (page: number = 1, isLoadMore: boolean = false) => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setAllGiftCardDetails([]);
    }
    setError(null);

    try {
      const queryParams: any = { email: email.trim(), page, limit: pageSize };
      if (statusFilter) queryParams.status = statusFilter;
      if (refundedFilter) queryParams.is_refunded = refundedFilter;
      if (brandFilter) queryParams.brand = brandFilter;
      if (purchaseTypeFilter) queryParams.purchase_type = purchaseTypeFilter;
      if (paymentTypeFilter) queryParams.payment_type = paymentTypeFilter;
      if (initiatedByFilter) queryParams.initiated_by = initiatedByFilter;
      const config = getConfigGet(queryParams);
      const response = await axios.get<GiftCardDetailsResponse>(ENDPOINTS.GIFT_CARD_DETAILS_PAGE, config);

      // Extract header and data from response
      if (!response.data || !response.data.data || !Array.isArray(response.data.data)) {
        throw new Error("Invalid response format: data array is missing");
      }

      const responseData = response.data.data;

      // Get header and total count from first page
      if (!isLoadMore) {
        if (responseData.length === 0) {
          throw new Error("No data found in response");
        }
        setTableHeader(responseData[0]);
        setTotalCount(response.data.total);
      }

      // Skip the header and get data rows
      const dataRows = responseData.slice(1) || [];

      if (isLoadMore) {
        setAllGiftCardDetails(prev => [...prev, ...dataRows]);
      } else {
        setAllGiftCardDetails(dataRows);
      }

      setCurrentPage(page);
      setHasSearched(true);

      // Check if there's more data to load
      const totalLoaded = isLoadMore ? (allGiftCardDetails?.length || 0) + dataRows.length : dataRows.length;
      setHasMoreData(totalLoaded < response.data.total);

      if (dataRows.length === 0 && !isLoadMore) {
        notify("No gift card details found for this email", "info");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to fetch gift card details";
      setError(errorMessage);
      notify(errorMessage, "error");
      if (!isLoadMore) {
        setAllGiftCardDetails([]);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setHasMoreData(false);
    setTableHeader(null);
    fetchGiftCardDetails(1);
    fetchBrandOptions();
  };

  const STATUS_LABELS: Record<string, string> = {
    new: "New", authorizing: "Authorizing", authorized: "Authorized",
    executed: "Executed", rejected: "Rejected",
  };

  // Removable chips for every applied filter so it's obvious what's active.
  // Clearing a chip only updates state — the user re-runs Search to apply it,
  // matching the explicit-search behaviour of the rest of the page.
  const activeFilters = [
    statusFilter && { key: "status", label: `Status: ${STATUS_LABELS[statusFilter] || statusFilter}`, clear: () => setStatusFilter("") },
    refundedFilter && { key: "refunded", label: `Refunded: ${refundedFilter === "1" ? "Yes" : "No"}`, clear: () => setRefundedFilter("") },
    brandFilter && { key: "brand", label: `Brand: ${brandFilter}`, clear: () => setBrandFilter("") },
    purchaseTypeFilter && { key: "type", label: `Type: ${purchaseTypeFilter === "one_time" ? "One Time" : "Top Up"}`, clear: () => setPurchaseTypeFilter("") },
    paymentTypeFilter && { key: "payment_type", label: `Category: ${paymentTypeFilter === "purchase" ? "Gift Card" : paymentTypeFilter === "top_up" ? "Top-Up" : "Mastercard Anywhere"}`, clear: () => setPaymentTypeFilter("") },
    initiatedByFilter && { key: "initiated_by", label: `Initiated By: ${initiatedByFilter === "manual" ? "Manual" : "Auto"}`, clear: () => setInitiatedByFilter("") },
  ].filter(Boolean) as { key: string; label: string; clear: () => void }[];

  const handleClearFilters = () => {
    setStatusFilter("");
    setRefundedFilter("");
    setBrandFilter("");
    setPurchaseTypeFilter("");
    setPaymentTypeFilter("");
    setInitiatedByFilter("");
  };

  // Brand dropdown is populated from the distinct brands the searched user
  // actually has transactions for (rather than the whole catalogue). Called on
  // every Search so the options reflect the current user.
  const fetchBrandOptions = async () => {
    if (!email.trim()) return;
    try {
      const config = getConfigGet({ email: email.trim(), fetch_brands: "true" });
      const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_DETAILS_PAGE, config);
      setBrandOptions(response.data?.brands || []);
    } catch {
      setBrandOptions([]);
    }
  };

  // Clear the brand filter if the current selection isn't in the new options
  // list (e.g. after switching the email).
  useEffect(() => {
    if (brandFilter && brandOptions.length > 0 && !brandOptions.includes(brandFilter)) {
      setBrandFilter("");
    }
  }, [brandOptions, brandFilter]);

  const handleLoadMore = () => {
    fetchGiftCardDetails(currentPage + 1, true);
  };

  const fetchProviderStatus = async (transactionId: number, transactionType?: string | null) => {
    setSelectedTransactionId(transactionId);
    setProviderStatusModalOpen(true);
    setProviderStatusLoading(true);
    setProviderStatusData(null);
    setProviderStatusError(null);

    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const typeQuery = transactionType === "mastercard_anywhere" ? "?type=mastercard_anywhere" : "";
      const response = await axios.get(
        `${ENDPOINTS.TRANSACTION_PROVIDER_STATUS}/${transactionId}${typeQuery}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: { transaction_type: transactionType },
        }
      );
      setProviderStatusData(response.data);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || err.message || "Failed to fetch provider status";
      setProviderStatusError(errorMessage);
    } finally {
      setProviderStatusLoading(false);
    }
  };

  const handleCloseProviderStatusModal = () => {
    setProviderStatusModalOpen(false);
    setProviderStatusData(null);
    setProviderStatusError(null);
    setSelectedTransactionId(null);
  };

  const handleBalanceClick = async (detail: GiftCardDetail) => {
    const key = getRowKey(detail);
    if (detail.has_balance_check && detail.gift_card_purchase_id) {
      setBalanceResults((prev) => ({
        ...prev,
        [key]: { loading: true, balance: null, unavailable: false },
      }));

      try {
        const accessToken = localStorage.getItem("accessToken") || "";
        const response = await axios.get(
          ENDPOINTS.ADMIN_GIFT_CARD_BALANCE_CHECK,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
            params: { gift_card_purchase_id: detail.gift_card_purchase_id, user_id: detail.user_id },
          }
        );
        const resData: any = response.data;
        const result = resData?.data?.[0] || resData?.[0] || resData;

        if (result?.card_balance !== null && result?.card_balance !== undefined) {
          setBalanceResults((prev) => ({
            ...prev,
            [key]: { loading: false, balance: result.card_balance, unavailable: false },
          }));
        } else {
          setBalanceResults((prev) => ({
            ...prev,
            [key]: { loading: false, balance: null, unavailable: true },
          }));
        }
      } catch {
        setBalanceResults((prev) => ({
          ...prev,
          [key]: { loading: false, balance: null, unavailable: true },
        }));
        notify("Failed to check balance", "error");
      }
    } else if (!detail.has_balance_check && detail.balance_enquiry_url) {
      window.open(detail.balance_enquiry_url, "_blank");
    }
  };

  // const getStatusChip = (transactionStatus: string | null, purchaseStatus: string, isCancelled: boolean | null, isRefunded: boolean | null) => {
  //   if (isCancelled) {
  //     return <Chip label="Cancelled" color="secondary" size="small" className={classes.statusChip} />;
  //   }
  //   if (isRefunded) {
  //     return <Chip label="Refunded" color="secondary" size="small" className={classes.statusChip} />;
  //   }

  //   const status = purchaseStatus || transactionStatus || "unknown";
  //   const statusColor = status === "success" ? "primary" : status === "pending" ? "default" : status === "failed" ? "secondary" : "default";
  //   return <Chip label={status} color={statusColor} size="small" className={classes.statusChip} />;
  // };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    // Remove the 'Z' and milliseconds, then replace 'T' with space
    return dateString.replace(/\.\d{3}Z$/, '').replace('T', ' ');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  // const formatBoolean = (value: boolean | null) => {
  //   if (value === null) return "N/A";
  //   return value ? "Yes" : "No";
  // };

  const getBooleanChip = (value: boolean | null, trueColor: "primary" | "secondary" = "primary") => {
    if (value === null) {
      return <Chip label="N/A" color="default" size="small" className={classes.statusChip} />;
    }
    return (
      <Chip
        label={value ? "Yes" : "No"}
        color={value ? trueColor : "default"}
        size="small"
        className={classes.statusChip}
      />
    );
  };

  const handleDownload = () => {
    if (!allGiftCardDetails?.length || !tableHeader) return;

    const allKeys = Object.keys(tableHeader).filter((k) => !HIDDEN_KEYS.has(k));
    const rows = allGiftCardDetails.map((detail: any) => {
      const row: any = {};
      allKeys.forEach((key) => {
        const header = tableHeader[key];
        switch (key) {
          case 'AMOUNT':
            row[header] = detail['amount'] ?? '';
            break;
          case 'REWARDS_AMOUNT':
            row[header] = detail['rewards_amount'] != null ? detail['rewards_amount'] : '';
            break;
          case 'IS_SPRIVE_CANCELLED':
            row[header] = detail.sprive_cancelled === 1 ? 'Yes' : detail.sprive_cancelled === 0 ? 'No' : 'N/A';
            break;
          case 'REFUNDED':
            row[header] = detail.is_refunded === 1 ? 'Yes' : detail.is_refunded === 0 ? 'No' : 'N/A';
            break;
          case 'IN_FLIGHT':
            row[header] = detail.is_inflight === 1 ? 'Yes' : detail.is_inflight === 0 ? 'No' : 'N/A';
            break;
          default: {
            const fieldMap: Record<string, string> = {
              ID: 'id', USER_ID: 'user_id', BRAND: 'brand_name',
              PAYMENT_CREATED_DATE: 'payment_created_date', TRANSACTION_STATUS: 'transaction_status',
              RYFT_PAYMENT_STATUS: 'ryft_payment_status', SIMP_ID: 'simp_id',
              SESSSION_ID: 'provider_session_id', PROVIDER: 'remitter_provider_id',
              BANK_REFERENCE: 'bank_reference', REFERENCE: 'reference',
              SOURCE: 'source', PAYMENT_AUTHORISED_DATE: 'payment_authorised_date',
              PAYMENT_EXECUTION_DATE: 'payment_execution_date', CLIENT_REF: 'client_reference_id',
              PURCHASE_STATUS: 'purchase_status', TOPUP_STATUS: 'topup_status',
              URL: 'url', GIFT_CARD_PURCHASE_ID: 'gift_card_purchase_id',
            };
            row[header] = detail[fieldMap[key] || key] ?? 'N/A';
          }
        }
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Gift Card Transactions');
    XLSX.writeFile(wb, `gift_card_transactions_${email.trim()}.xlsx`);
  };

  return (
    <div className={classes.main}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Gift Card Transaction Details
          </Typography>

          <div className={classes.form}>
            <div className={classes.filterRow}>
              <TextField
                className={classes.input}
                label="Email Address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                variant="outlined"
                size="small"
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as string)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="new">New</MenuItem>
                  <MenuItem value="authorizing">Authorizing</MenuItem>
                  <MenuItem value="authorized">Authorized</MenuItem>
                  <MenuItem value="executed">Executed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Refunded</InputLabel>
                <Select
                  value={refundedFilter}
                  onChange={(e) => setRefundedFilter(e.target.value as string)}
                  label="Refunded"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  <MenuItem value="0">No</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
                <InputLabel>Brand</InputLabel>
                <Select
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value as string)}
                  label="Brand"
                  disabled={brandOptions.length === 0}
                >
                  <MenuItem value="">All</MenuItem>
                  {brandOptions.map((b) => (
                    <MenuItem key={b} value={b}>{b}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={purchaseTypeFilter}
                  onChange={(e) => setPurchaseTypeFilter(e.target.value as string)}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="one_time">One Time</MenuItem>
                  <MenuItem value="top_up">Top Up</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={paymentTypeFilter}
                  onChange={(e) => setPaymentTypeFilter(e.target.value as string)}
                  label="Category"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="purchase">Gift Card</MenuItem>
                  <MenuItem value="top_up">Top-Up</MenuItem>
                  <MenuItem value="mastercard_anywhere">Mastercard Anywhere</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
                <InputLabel>Initiated By</InputLabel>
                <Select
                  value={initiatedByFilter}
                  onChange={(e) => setInitiatedByFilter(e.target.value as string)}
                  label="Initiated By"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="manual">Manual</MenuItem>
                  <MenuItem value="auto">Auto</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  label="Page Size"
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={200}>200</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading || !email.trim()}
                startIcon={loading && <CircularProgress size={20} />}
              >
                Search
              </Button>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                disabled={activeFilters.length === 0}
              >
                Clear Filters
              </Button>
            </div>

            {activeFilters.length > 0 && (
              <div className={classes.activeFilters}>
                <span className={classes.activeFiltersLabel}>Active filters:</span>
                {activeFilters.map((f) => (
                  <Chip
                    key={f.key}
                    label={f.label}
                    size="small"
                    onDelete={f.clear}
                  />
                ))}
              </div>
            )}
          </div>

          {error && (
            <Typography color="error" style={{ marginTop: 16, padding: 16, backgroundColor: "#ffebee", borderRadius: 4 }}>
              {error}
            </Typography>
          )}

          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Loading all gift card details...
              </Typography>
            </div>
          )}

          {hasSearched && !loading && (allGiftCardDetails?.length || 0) === 0 && (
            <div className={classes.noDataContainer}>
              <Typography variant="h6">No gift card details found</Typography>
            </div>
          )}

          {(allGiftCardDetails?.length || 0) > 0 && (
            <>
              <Box style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16, marginBottom: 8 }}>
                <Typography variant="h6">
                  Showing {allGiftCardDetails?.length || 0} of {totalCount} gift card(s)
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownload}
                >
                  Download
                </Button>
              </Box>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      {tableHeader ? (() => {
                        const allKeys = Object.keys(tableHeader);
                        const mainKeys = STICKY_KEYS.concat(
                          allKeys.filter((k) => !EXPANDABLE_KEYS.has(k) && !STICKY_KEYS.includes(k) && !HIDDEN_KEYS.has(k))
                        );
                        return mainKeys.map((key, index) => {
                          const isFrozen = index < FROZEN_COLUMN_COUNT;
                          return (
                            <TableCell
                              key={key}
                              className={`${classes.tableCell} ${isFrozen ? classes.stickyHeaderCell : ""}`}
                              style={isFrozen ? {
                                left: FROZEN_LEFT_OFFSETS[index],
                                width: FROZEN_COLUMN_WIDTHS[index],
                                borderRight: index === FROZEN_COLUMN_COUNT - 1 ? "2px solid #ccc" : undefined,
                              } : undefined}
                            >
                              {tableHeader[key]}
                            </TableCell>
                          );
                        });
                      })() : null}
                      <TableCell className={classes.tableCell} style={{ width: 220 }}>Actions</TableCell>
                      {showExtraColumns && tableHeader ? (() => {
                        const allKeys = Object.keys(tableHeader);
                        const extraKeys = allKeys.filter((k) => EXPANDABLE_KEYS.has(k) && !HIDDEN_KEYS.has(k));
                        return extraKeys.map((key) => (
                          <TableCell key={key} className={classes.tableCell}>
                            {tableHeader[key]}
                          </TableCell>
                        ));
                      })() : null}
                      <TableCell className={`${classes.tableCell} ${classes.toggleColumnsBtn}`} style={{ width: 48 }}>
                        <IconButton size="small" onClick={() => setShowExtraColumns((prev) => !prev)}>
                          {showExtraColumns ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(allGiftCardDetails || []).map((detail) => {
                      const allKeys = tableHeader ? Object.keys(tableHeader) : [];
                      const mainKeys = STICKY_KEYS.concat(
                        allKeys.filter((k) => !EXPANDABLE_KEYS.has(k) && !STICKY_KEYS.includes(k) && !HIDDEN_KEYS.has(k))
                      );
                      const extraKeys = allKeys.filter((k) => EXPANDABLE_KEYS.has(k) && !HIDDEN_KEYS.has(k));

                      const renderCellContent = (key: string) => {
                        switch (key) {
                          case 'ID':
                            return detail.id;
                          case 'USER_ID':
                            return detail.user_id;
                          case 'BRAND':
                            if (detail.transaction_type === 'top_up') {
                              return (
                                <Box style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 12, color: "#666" }}>
                                  <span>↳</span>
                                  <span>{detail.brand_name || "N/A"}</span>
                                </Box>
                              );
                            }
                            if (detail.transaction_type === 'mastercard_anywhere') {
                              return (
                                <Box style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <span style={{ fontSize: "0.75rem", background: "#1565c0", color: "#fff", borderRadius: 3, padding: "1px 4px", fontWeight: 600 }}>MC</span>
                                  <span>{detail.brand_name || "Mastercard Anywhere"}</span>
                                </Box>
                              );
                            }
                            return detail.brand_name || "N/A";
                          case 'AMOUNT':
                            return formatCurrency(parseFloat(detail.amount.toString()));
                          case 'PAYMENT_CREATED_DATE':
                            return formatDate(detail.payment_created_date || "");
                          case 'TRANSACTION_STATUS':
                            return detail.transaction_status || "N/A";
                          case 'GIFT_CARD_PURCHASE_ID':
                            return detail.gift_card_purchase_id ? (
                              <Box
                                className={classes.copyableText}
                                onClick={() => handleCopy(String(detail.gift_card_purchase_id))}
                                title="Click to copy — the parent purchase this row belongs to"
                              >
                                {detail.gift_card_purchase_id}
                              </Box>
                            ) : "N/A";
                          case 'RYFT_PAYMENT_STATUS':
                            return detail.ryft_payment_status || "N/A";
                          case 'SIMP_ID':
                            return detail.simp_id ? (
                              <Box
                                className={classes.copyableText}
                                onClick={() => handleCopy(detail.simp_id)}
                                title="Click to copy"
                              >
                                {detail.simp_id}
                              </Box>
                            ) : "N/A";
                          case 'SESSSION_ID':
                            return (
                              <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {detail.provider_session_id || "N/A"}
                              </Box>
                            );
                          case 'PROVIDER':
                            return (
                              <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {detail.remitter_provider_id || "N/A"}
                              </Box>
                            );
                          case 'BANK_REFERENCE':
                            return detail.bank_reference ? (
                              <Box
                                className={classes.copyableText}
                                onClick={() => handleCopy(detail.bank_reference!.toUpperCase())}
                                title="Click to copy"
                              >
                                {detail.bank_reference.toUpperCase()}
                              </Box>
                            ) : "N/A";
                          case 'REFERENCE':
                            return (
                              <Box style={{ maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis" }}>
                                {detail.reference || "N/A"}
                              </Box>
                            );
                          case 'IS_SPRIVE_CANCELLED':
                            return getBooleanChip(detail.sprive_cancelled === 1, "secondary");
                          case 'REFUNDED':
                            return getBooleanChip(detail.is_refunded === 1, "secondary");
                          case 'SOURCE':
                            return detail.source || "N/A";
                          case 'PAYMENT_AUTHORISED_DATE':
                            return formatDate(detail.payment_authorised_date || "");
                          case 'PAYMENT_EXECUTION_DATE':
                            return formatDate(detail.payment_execution_date || "");
                          case 'CLIENT_REF':
                            return detail.client_reference_id ? (
                              <Box
                                className={classes.copyableText}
                                onClick={() => handleCopy(detail.client_reference_id!)}
                                title="Click to copy"
                              >
                                {detail.client_reference_id}
                              </Box>
                            ) : "N/A";
                          case 'PURCHASE_STATUS':
                            return detail.purchase_status || "N/A";
                          case 'TOPUP_STATUS':
                            return detail.topup_status ? (
                              <Chip
                                label={detail.topup_status}
                                size="small"
                                className={classes.statusChip}
                                style={{
                                  backgroundColor: detail.topup_status === 'success' ? '#e8f5e9' : detail.topup_status === 'failed' ? '#ffebee' : undefined,
                                  color: detail.topup_status === 'success' ? '#2e7d32' : detail.topup_status === 'failed' ? '#c62828' : undefined,
                                }}
                              />
                            ) : "N/A";
                          case 'REWARDS_AMOUNT':
                            return detail.rewards_amount != null ? formatCurrency(parseFloat(detail.rewards_amount.toString())) : "N/A";
                          case 'IN_FLIGHT':
                            return getBooleanChip(detail.is_inflight === 1, "primary");
                          case 'URL':
                            return detail.url ? (
                              <Button
                                size="small"
                                color="primary"
                                onClick={() => window.open(detail.url!, "_blank")}
                              >
                                View
                              </Button>
                            ) : (
                              "N/A"
                            );
                          default:
                            return "N/A";
                        }
                      };

                      return (
                        <TableRow key={getRowKey(detail)} hover>
                          {mainKeys.map((key, index) => {
                            const isFrozen = index < FROZEN_COLUMN_COUNT;
                            return (
                              <TableCell
                                key={key}
                                className={`${classes.tableCell} ${isFrozen ? classes.stickyCell : ""}`}
                                style={isFrozen ? {
                                  left: FROZEN_LEFT_OFFSETS[index],
                                  width: FROZEN_COLUMN_WIDTHS[index],
                                  borderRight: index === FROZEN_COLUMN_COUNT - 1 ? "2px solid #ccc" : undefined,
                                } : undefined}
                              >
                                {renderCellContent(key)}
                              </TableCell>
                            );
                          })}
                          <TableCell className={classes.tableCell} style={{ width: 220 }}>
                            <Box style={{ display: "flex", gap: 4 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => fetchProviderStatus(detail.id, detail.transaction_type)}
                              >
                                Status
                              </Button>
                              {(() => {
                                const br = balanceResults[getRowKey(detail)];
                                if (br?.loading) {
                                  return <CircularProgress size={20} />;
                                }
                                if (br?.balance !== null && br?.balance !== undefined) {
                                  return (
                                    <Chip
                                      label={formatCurrency(br.balance)}
                                      size="small"
                                      style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontWeight: 600 }}
                                    />
                                  );
                                }
                                if (br?.unavailable) {
                                  return (
                                    <Box style={{ display: "inline-flex", alignItems: "center" }} title="Balance check not available">
                                      <CancelIcon style={{ color: "#bdbdbd", fontSize: 20 }} />
                                    </Box>
                                  );
                                }
                                if (
                                  (detail.has_balance_check && detail.gift_card_purchase_id) ||
                                  (!detail.has_balance_check && detail.balance_enquiry_url && detail.transaction_type !== 'top_up')
                                ) {
                                  return (
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      style={{ color: "#4caf50", borderColor: "#4caf50" }}
                                      onClick={() => handleBalanceClick(detail)}
                                    >
                                      Balance
                                    </Button>
                                  );
                                }
                                return (
                                  <Box style={{ display: "inline-flex", alignItems: "center" }} title="No purchase transaction">
                                    <CancelIcon style={{ color: "#bdbdbd", fontSize: 20 }} />
                                  </Box>
                                );
                              })()}
                            </Box>
                          </TableCell>
                          {showExtraColumns && extraKeys.map((key) => (
                            <TableCell key={key} className={classes.tableCell}>
                              {renderCellContent(key)}
                            </TableCell>
                          ))}
                          <TableCell className={`${classes.tableCell} ${classes.toggleColumnsBtnBody}`} style={{ width: 48 }} />
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More Button */}
              {hasMoreData && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}

              {/* Results Summary */}
              {hasSearched && !loading && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing {allGiftCardDetails?.length || 0} of {totalCount} transactions
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Provider Status Modal */}
      <Dialog
        open={providerStatusModalOpen}
        onClose={handleCloseProviderStatusModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            Provider Status {selectedTransactionId ? `(Transaction ID: ${selectedTransactionId})` : ""}
          </Typography>
          <IconButton onClick={handleCloseProviderStatusModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          {providerStatusLoading && (
            <div className={classes.modalLoadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Fetching provider status...
              </Typography>
            </div>
          )}
          {providerStatusError && (
            <Typography color="error" style={{ padding: 16, backgroundColor: "#ffebee", borderRadius: 4 }}>
              {providerStatusError}
            </Typography>
          )}
          {providerStatusData && !providerStatusLoading && (
            <pre className={classes.jsonContent}>
              {JSON.stringify(uppercaseDisplayFields(providerStatusData), null, 2)}
            </pre>
          )}
        </DialogContent>
        <DialogActions style={{marginRight: 16, marginBottom: 16}}>
          <Button onClick={handleCloseProviderStatusModal} color="primary" variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default GiftCardTransactionDetailsPage;